import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectCartItems, fetchCartItems } from '../../../../redux/slices/cartSlice';
import {
  fetchAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  selectAddresses,
  selectAddressStatus,
  selectDefaultShippingAddress,
  selectDefaultBillingAddress,
} from '../../../../redux/slices/addressSlice';
import AddressForm from '../../../../component/library/addressForm';
import AlertPopup from '../../../../component/library/alertPopup';
import DeletePopUp from '../../../../component/library/deletePopUp';
import { CheckOutBox } from '../cart/CheckOutBox';
import ShippingMethodSelector from './ShippingMethodSelector';
import { FiMapPin, FiEdit2, FiTrash2, FiPlus, FiCheck } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import { FaCoins, FaInfoCircle } from 'react-icons/fa';
import { warehouseService } from '../../../../service/website/warehouseService';
// import { imPointsApiService } from '../../../../service/admin/imPointsApiService';
import { CartApiService } from '../../../service/cartApiService';
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import './style.css';
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";

export default function Address() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartData = useSelector(selectCartItems);
  const savedAddresses = useSelector(selectAddresses);
  const addressStatus = useSelector(selectAddressStatus);
  const defaultShippingAddress = useSelector(selectDefaultShippingAddress);
  const defaultBillingAddress = useSelector(selectDefaultBillingAddress);
  const {
    shippingMethod,
    setShippingMethod,
    totalLMDCharges,
    gstOnLMD,
    totalLMDWithGST,
  } = useOutletContext() || {};

  // Track if we've initialized the default selection
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

  const [cart, setCart] = useState([]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const message = useMessage();

  // Address selection state
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState(null);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(null);
  const [useSameAddressForBilling, setUseSameAddressForBilling] = useState(false);

  // Warehouse state for self-pickup
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [currentAddress, setCurrentAddress] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);

  // Order data
  const [orderData, setOrderData] = useState({
    buyDirect: false,
    productId: '',
    quantity: '',
  });

  // IM Points
  // const [imPoints, setImPoints] = useState(null);
  // const [imTooltipVisible, setImTooltipVisible] = useState(false);
  // const imTooltipRef = useRef(null);

  // Get stored data from navigation state
  const priceSummaryTotals = useMemo(
    () => location.state?.priceSummaryTotals || null,
    [location.state]
  );
  const priceSummaryEntries = location.state?.priceSummaryEntries || {};

  // Calculate totals from cart data when navigation state is not available
  const calculatedTotals = useMemo(() => {
    if (priceSummaryTotals) return priceSummaryTotals;

    // Calculate from cart data
    const cartTotalFinalPrice = cart.reduce((sum, item) => {
      return sum + (item.product?.variants?.reduce((variantSum, variant) => {
        return variantSum + ((variant.finalPrice || variant.price || 0) * (variant.quantity || 0));
      }, 0) || 0);
    }, 0);

    return {
      cartTotalFinalPrice,
      cartTotalLMD: 0, // Will be calculated by API
      cartLMD: 0, // Will be calculated by API
      TOTAL_PRICE: cartTotalFinalPrice
    };
  }, [priceSummaryTotals, cart]);

  // Create fallback navigation state for CheckOutBox when direct navigation occurs
  const fallbackState = useMemo(() => {
    if (priceSummaryTotals) return null; // Already have proper state

    // Create fallback state from calculated totals
    return {
      priceSummaryTotals: calculatedTotals,
      priceSummaryEntries: cart.reduce((entries, item, index) => {
        const entryTotal = item.product?.variants?.reduce((variantSum, variant) => {
          return variantSum + ((variant.finalPrice || variant.price || 0) * (variant.quantity || 0));
        }, 0) || 0;

        entries[`item_${index}`] = {
          TOTAL_PRICE: entryTotal,
          product: item.product,
          variants: item.variants
        };
        return entries;
      }, {})
    };
  }, [priceSummaryTotals, calculatedTotals, cart]);

  // Fetch addresses and cart data on mount
  useEffect(() => {
    dispatch(fetchAddresses());
    dispatch(fetchCartItems());
  }, [dispatch]);

  // Fetch IM Points
  // useEffect(() => {
  //   imPointsApiService.getUserIMPoints()
  //     .then((res) => {
  //       if (res?.status?.code === 200 && res?.data) {
  //         setImPoints(res.data);
  //       }
  //     })
  //     .catch(() => {});
  // }, []);

  // const imAddressPreview = useMemo(() => {
  //   if (!imPoints) return null;
  //   // Match CheckOutBox "Final Total" = Grand Total + shipping (with GST), not raw LMD from summary alone
  //   const grandTotalInclGst = Number(priceSummaryTotals?.TOTAL_PRICE || 0);
  //   const shippingAmount = Number(
  //     totalLMDWithGST || totalLMDCharges || priceSummaryTotals?.LMD || 0
  //   );
  //   const finalOrderTotal = parseFloat((grandTotalInclGst + shippingAmount).toFixed(2));
  //   const isExpired = !!(imPoints.expiresAt && new Date(imPoints.expiresAt) < new Date());
  //   // % applies to Grand Total (incl. GST) only — shipping excluded from percentage base
  //   const pct = !isExpired ? Number(imPoints.percentage) || 0 : 0;
  //   const cap =
  //     !isExpired && pct > 0 && grandTotalInclGst > 0
  //       ? parseFloat(((grandTotalInclGst * pct) / 100).toFixed(2))
  //       : 0;
  //   const pts = !isExpired ? Number(imPoints.points) || 0 : 0;
  //   const discount =
  //     !isExpired && pct > 0 && pts > 0 && finalOrderTotal > 0
  //       ? Math.min(pts, cap, finalOrderTotal)
  //       : 0;
  //   const payableAfterIm = parseFloat(Math.max(0, finalOrderTotal - discount).toFixed(2));
  //   return {
  //     isExpired,
  //     discount,
  //     payableAfterIm,
  //     grandTotalInclGst,
  //     shippingAmount,
  //     finalOrderTotal,
  //     pts,
  //     pct,
  //   };
  // }, [imPoints, priceSummaryTotals, totalLMDWithGST, totalLMDCharges]);

  // Fetch active warehouses when self-pickup is selected
  useEffect(() => {
    if (shippingMethod === 'self-pickup') {
      fetchActiveWarehouses();
    }
  }, [shippingMethod]);

  const fetchActiveWarehouses = async () => {
    setLoadingWarehouses(true);
    try {
      const response = await warehouseService.getActiveWarehouses();
      if (response?.status?.code === 200) {
        setWarehouses(response.data || []);
        // Auto-select first warehouse if available
        if (response.data && response.data.length > 0 && !selectedWarehouseId) {
          setSelectedWarehouseId(response.data[0]._id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  // Set default addresses when they're loaded (only once after initial fetch)
  useEffect(() => {
    // Only initialize defaults once after addresses are successfully loaded
    if (addressStatus === 'succeeded' && savedAddresses.length > 0 && !hasInitializedDefaults) {
      // Set shipping address: prefer default shipping, then first address
      const shippingDefault = defaultShippingAddress || savedAddresses[0];
      if (shippingDefault) {
        setSelectedShippingAddressId(shippingDefault._id);
      }

      // Set billing address: prefer default billing, then first address
      const billingDefault = defaultBillingAddress || savedAddresses[0];
      if (billingDefault) {
        setSelectedBillingAddressId(billingDefault._id);
      }

      setHasInitializedDefaults(true);
    }
  }, [addressStatus, savedAddresses, defaultShippingAddress, defaultBillingAddress, hasInitializedDefaults]);

  // Set billing address to shipping when "same address" is checked
  useEffect(() => {
    if (useSameAddressForBilling && selectedShippingAddressId) {
      setSelectedBillingAddressId(selectedShippingAddressId);
    }
  }, [useSameAddressForBilling, selectedShippingAddressId]);

  // Handle cart data
  useEffect(() => {
    if (searchParams.get('product')) {
      const arr = cartData.filter(product => product.product._id === searchParams.get('product'));
      setCart(arr);
    } else {
      setCart(cartData);
    }
  }, [cartData, searchParams]);

  // Handle buy direct
  useEffect(() => {
    if (searchParams.get('product') && cart.length > 0) {
      setOrderData({
        buyDirect: true,
        productId: cart[0]?.product?._id,
        quantity: cart[0]?.quantity,
      });
    }
  }, [searchParams, cart]);

  // Get selected addresses (memoized so downstream effects — e.g. abandoned lead — do not re-run every render)
  const shippingAddress = useMemo(
    () => savedAddresses.find(addr => addr._id === selectedShippingAddressId),
    [savedAddresses, selectedShippingAddressId]
  );
  const billingAddress = useMemo(() => {
    if (useSameAddressForBilling) return shippingAddress;
    return savedAddresses.find(addr => addr._id === selectedBillingAddressId);
  }, [useSameAddressForBilling, savedAddresses, selectedBillingAddressId, shippingAddress]);

  const buildLeadItemsFromCheckoutCart = useCallback((items = []) => {
    const leadItems = [];
    (Array.isArray(items) ? items : []).forEach((cartItem) => {
      const variants = Array.isArray(cartItem?.variants) ? cartItem.variants : [];
      const productId = cartItem?.product?._id || cartItem?.productId || '';
      // const pricingMode = cartItem?.pricingMode || 'dropshipping';
      const pricingMode = cartItem?.pricingMode ? ((cartItem.pricingMode.toLowerCase() === 'dropshipping') ? '1-Piece' : cartItem.pricingMode) : '1-Piece';

      if (variants.length > 0) {
        variants.forEach((variant) => {
          leadItems.push({
            productId,
            sku: variant?.skuId || variant?.sku || cartItem?.product?.sku || '',
            specId: variant?.specId || '',
            quantity: Number(variant?.quantity) || 1,
            pricingMode,
          });
        });
        return;
      }

      leadItems.push({
        productId,
        sku: cartItem?.product?.sku || '',
        specId: '',
        quantity: Number(cartItem?.quantity) || 1,
        pricingMode,
      });
    });
    return leadItems.filter((item) => item.productId || item.sku || item.specId);
  }, []);

  const effectiveLMDForLead =
    shippingMethod === 'self-pickup' ? 0 : totalLMDWithGST || totalLMDCharges || 0;

  // Payment-stage lead: first checkout step after the bag (/checkout/address). The payment page does not call this
  // API (avoids duplicates). Backend deletes open cart-stage rows for this user when this runs.
  // Wait until fulfillment has a selected shipping address so we do not fire once with empty addresses and again after defaults load (duplicate network calls).
  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (!auth.isLoggedIn || !auth.loginHeader) return;
    if (!Array.isArray(cart) || cart.length === 0) return;
    if (shippingMethod === 'fulfillment' && !selectedShippingAddressId) return;

    const productsTotal = Number(priceSummaryTotals?.TOTAL_PRICE || 0);
    const cartValue = productsTotal + effectiveLMDForLead;
    if (!cartValue || cartValue <= 0) return;

    const leadItems = buildLeadItemsFromCheckoutCart(cart);
    if (!leadItems.length) return;

    const buildAddr = (addr) =>
      addr
        ? {
          firstName: addr.firstName || '',
          lastName: addr.lastName || '',
          phone: addr.phone || '',
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          city: addr.city || '',
          state: addr.state || '',
          pincode: addr.pincode || '',
        }
        : null;

    const t = setTimeout(() => {
      const ship = savedAddresses.find((addr) => addr._id === selectedShippingAddressId);
      const bill = useSameAddressForBilling
        ? ship
        : savedAddresses.find((addr) => addr._id === selectedBillingAddressId);
      CartApiService.recordAbandonedLead({
        stage: 'payment',
        cartValue,
        totalLMDCharges: effectiveLMDForLead,
        gstOnLMD: gstOnLMD || 0,
        shippingAddress: buildAddr(ship),
        billingAddress: buildAddr(bill || ship),
        leadItems,
      }).catch(() => { });
    }, 800);

    return () => clearTimeout(t);
  }, [
    cart,
    priceSummaryTotals,
    effectiveLMDForLead,
    gstOnLMD,
    shippingMethod,
    selectedShippingAddressId,
    selectedBillingAddressId,
    useSameAddressForBilling,
    savedAddresses,
    buildLeadItemsFromCheckoutCart,
  ]);

  // Format address for display
  const formatAddress = address => {
    if (!address) return '';
    const parts = [address.address1, address.address2, address.city, address.state, address.pincode].filter(Boolean);
    return parts.join(', ');
  };

  // Handle add new address
  const handleAddNewAddress = () => {
    setModalMode('add');
    setCurrentAddress(null);
    setShowAddressModal(true);
  };

  // Handle edit address
  const handleEditAddress = address => {
    setModalMode('edit');
    setCurrentAddress(address);
    setShowAddressModal(true);
  };

  // Handle delete address
  const handleDeleteAddress = addressId => {
    setAddressToDelete(addressId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (addressToDelete) {
      await dispatch(deleteAddress(addressToDelete));
      // Clear selection if deleted address was selected
      if (addressToDelete === selectedShippingAddressId) {
        setSelectedShippingAddressId(null);
      }
      if (addressToDelete === selectedBillingAddressId) {
        setSelectedBillingAddressId(null);
      }
      setShowDeleteModal(false);
      setAddressToDelete(null);
    }
  };

  // Handle address form submit
  const handleAddressSubmit = async formData => {
    if (modalMode === 'add') {
      const result = await dispatch(addAddress(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        const newAddressId = result.payload._id;
        // Auto-select as shipping if no shipping selected
        if (!selectedShippingAddressId) {
          setSelectedShippingAddressId(newAddressId);
        }
      }
    } else {
      await dispatch(
        updateAddress({
          addressId: currentAddress._id,
          addressData: formData,
        })
      );
    }
    setShowAddressModal(false);
    setCurrentAddress(null);
  };

  // Handle shipping address selection
  const handleSelectShipping = addressId => {
    setSelectedShippingAddressId(addressId);
    if (useSameAddressForBilling) {
      setSelectedBillingAddressId(addressId);
    }
  };

  // Handle billing address selection
  const handleSelectBilling = addressId => {
    setSelectedBillingAddressId(addressId);
  };

  // Proceed to payment
  const proceedToPayment = (summaryPayload = {}) => {
    // Only check for address if shipping method is fulfillment
    if (shippingMethod === 'fulfillment') {
      if (!selectedShippingAddressId) {
        // alert('Please select a shipping address');
        message.showToast("Please select a shipping address", ToastTypes.WARNING);
        return;
      }

      if (!useSameAddressForBilling && !selectedBillingAddressId) {
        // alert('Please select a billing address');
        message.showToast("Please select a billing address", ToastTypes.WARNING);
        return;
      }
    } else if (shippingMethod === 'self-pickup') {
      // Check for warehouse selection - Commented out since self-pickup is disabled
      // if (!selectedWarehouseId) {
      //   alert('Please select a pickup warehouse');
      //   return;
      // }
    }

    const finalBillingAddress = useSameAddressForBilling ? shippingAddress : billingAddress;

    // Prepare full shipping address object for order (only for fulfillment)
    const shippingAddressPayload = shippingMethod === 'fulfillment' && shippingAddress ? {
      firstName: shippingAddress.firstName,
      lastName: shippingAddress.lastName,
      phone: shippingAddress.phone,
      address1: shippingAddress.address1,
      address2: shippingAddress.address2 || '',
      city: shippingAddress.city,
      state: shippingAddress.state,
      pincode: shippingAddress.pincode,
      country: shippingAddress.country || 'India',
      addressType: shippingAddress.addressType || '',
    } : null;

    // Prepare full billing address object for order (only for fulfillment)
    const billingAddressPayload = shippingMethod === 'fulfillment' && finalBillingAddress ? {
      firstName: finalBillingAddress.firstName,
      lastName: finalBillingAddress.lastName,
      phone: finalBillingAddress.phone,
      address1: finalBillingAddress.address1,
      address2: finalBillingAddress.address2 || '',
      city: finalBillingAddress.city,
      state: finalBillingAddress.state,
      pincode: finalBillingAddress.pincode,
      country: finalBillingAddress.country || 'India',
      addressType: finalBillingAddress.addressType || '',
    } : null;

    // Get selected warehouse for self-pickup
    const selectedWarehouse = shippingMethod === 'self-pickup' && selectedWarehouseId
      ? warehouses.find(w => w._id === selectedWarehouseId)
      : null;

    // Prepare data for localStorage
    const checkoutData = {
      ...orderData,
      // Full address objects for backend
      shippingAddress: shippingAddressPayload,
      billingAddress: billingAddressPayload,
      useSameAddressForBilling: useSameAddressForBilling,
      shippingMethod: shippingMethod, // Add shipping method to checkout data
      // Warehouse data for self-pickup
      warehouse: selectedWarehouse ? {
        _id: selectedWarehouse._id,
        name: selectedWarehouse.name,
        address1: selectedWarehouse.address1,
        address2: selectedWarehouse.address2,
        city: selectedWarehouse.city,
        state: selectedWarehouse.state,
        pincode: selectedWarehouse.pincode,
        country: selectedWarehouse.country,
        phone: selectedWarehouse.phone,
        landmark: selectedWarehouse.landmark,
        instructions: selectedWarehouse.instructions,
      } : null,
      // Legacy fields for display on payment page (only for fulfillment)
      ...(shippingMethod === 'fulfillment' && shippingAddress ? {
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        phoneNo: shippingAddress.phone,
        address1: shippingAddress.address1,
        address2: shippingAddress.address2,
        city: shippingAddress.city,
        state: shippingAddress.state,
        pincode: shippingAddress.pincode,
        country: shippingAddress.country,
        addressType: shippingAddress.addressType,
        billingFirstName: finalBillingAddress.firstName,
        billingLastName: finalBillingAddress.lastName,
        billingPhone: finalBillingAddress.phone,
        billingAddress1: finalBillingAddress.address1,
        billingAddress2: finalBillingAddress.address2,
        billingCity: finalBillingAddress.city,
        billingState: finalBillingAddress.state,
        billingPincode: finalBillingAddress.pincode,
      } : {})
    };

    localStorage.setItem('cart', JSON.stringify(checkoutData));

    const summaryTotalsToPass = summaryPayload.priceSummaryTotals || priceSummaryTotals || null;
    const summaryEntriesToPass = summaryPayload.priceSummaryEntries || priceSummaryEntries || {};

    navigate('/checkout/payment', {
      state: {
        priceSummaryTotals: summaryTotalsToPass,
        priceSummaryEntries: summaryEntriesToPass,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left Side - Address Selection */}
          <div className="lg:col-span-2 space-y-4">
            {/* Shipping Method Selector - Commented out self-pickup option */}
            {/* <ShippingMethodSelector 
              selectedMethod={shippingMethod} 
              onMethodChange={setShippingMethod} 
            /> */}

            {/* Only show address selection if shipping method is fulfillment - Always show since self-pickup is disabled */}
            {/* {shippingMethod === 'fulfillment' && ( */}
            <>
              {/* Selected Addresses Cards - Compact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Shipping Address Card */}
                <div className="address-card-compact">
                  <div className="address-card-header">
                    <div className="address-card-icon shipping">
                      <FiMapPin size={14} />
                    </div>
                    <span className="address-card-title">Shipping</span>
                    {shippingAddress && <MdCheckCircle className="text-green-500" size={16} />}
                  </div>
                  {shippingAddress ? (
                    <div className="address-card-body">
                      <p className="address-card-name">
                        {shippingAddress.firstName} {shippingAddress.lastName}
                      </p>
                      <p className="address-card-text">{formatAddress(shippingAddress)}</p>
                      <p className="address-card-phone">{shippingAddress.phone}</p>
                    </div>
                  ) : (
                    <div className="address-card-empty">
                      <p>No address selected</p>
                    </div>
                  )}
                </div>

                {/* Billing Address Card */}
                <div className="address-card-compact">
                  <div className="address-card-header">
                    <div className="address-card-icon billing">
                      <FiMapPin size={14} />
                    </div>
                    <span className="address-card-title">Billing</span>
                    {useSameAddressForBilling && <span className="address-same-badge">Same as Shipping</span>}
                    {billingAddress && <MdCheckCircle className="text-green-500" size={16} />}
                  </div>
                  {billingAddress ? (
                    <div className="address-card-body">
                      <p className="address-card-name">
                        {billingAddress.firstName} {billingAddress.lastName}
                      </p>
                      <p className="address-card-text">{formatAddress(billingAddress)}</p>
                      <p className="address-card-phone">{billingAddress.phone}</p>
                    </div>
                  ) : (
                    <div className="address-card-empty">
                      <p>No address selected</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Same as Shipping Checkbox */}
              <div className="same-address-toggle">
                <label className="same-address-label">
                  <input type="checkbox" checked={useSameAddressForBilling} onChange={e => setUseSameAddressForBilling(e.target.checked)} className="same-address-checkbox" />
                  <span className="checkmark"></span>
                  <span>Use same address for billing</span>
                </label>
              </div>

              {/* Address List */}
              <div className="address-list-wrapper">
                {savedAddresses.length > 0 && (
                  <div className="address-list-header">
                    <h3 className="address-list-title">Select Address</h3>
                    <button onClick={handleAddNewAddress} className="address-add-btn" disabled={savedAddresses.length >= 10}>
                      <FiPlus size={16} />
                      <span>Add New</span>
                    </button>
                  </div>
                )}

                {savedAddresses.length === 0 ? (
                  <div className="address-form-inline">
                    <h3 className="address-list-title">Add Your First Address</h3>
                    <AddressForm
                      addressData={null}
                      onSubmit={handleAddressSubmit}
                      onCancel={() => {
                        // No cancel action needed when adding first address
                      }}
                      submitText="Add Address"
                    />
                  </div>
                ) : (
                  <div className="address-list">
                    {savedAddresses.map(address => {
                      const isShipping = address._id === selectedShippingAddressId;
                      const isBilling = address._id === selectedBillingAddressId && !useSameAddressForBilling;

                      return (
                        <div key={address._id} className={`address-list-item ${isShipping || isBilling ? 'selected' : ''}`}>
                          <div className="address-list-content">
                            <div className="address-list-top">
                              <span className="address-list-name">
                                {address.firstName} {address.lastName}
                              </span>
                              {address.label && <span className="address-list-label">{address.label}</span>}
                              {address.isDefaultShipping && address.isDefaultBilling && <span className="address-list-default">Default</span>}
                              {address.isDefaultShipping && !address.isDefaultBilling && <span className="address-list-default shipping">Default Shipping</span>}
                              {!address.isDefaultShipping && address.isDefaultBilling && <span className="address-list-default billing">Default Billing</span>}
                            </div>
                            <p className="address-list-address">{formatAddress(address)}</p>
                            <p className="address-list-phone">{address.phone}</p>

                            {/* Selection Buttons */}
                            <div className="address-list-actions">
                              <button onClick={() => handleSelectShipping(address._id)} className={`address-select-btn ${isShipping ? 'active' : ''}`}>
                                {isShipping ? <FiCheck size={12} /> : null}
                                Shipping
                              </button>
                              {!useSameAddressForBilling && (
                                <button onClick={() => handleSelectBilling(address._id)} className={`address-select-btn billing ${isBilling ? 'active' : ''}`}>
                                  {isBilling ? <FiCheck size={12} /> : null}
                                  Billing
                                </button>
                              )}
                              <div className="address-list-edit-actions">
                                <button onClick={() => handleEditAddress(address)} className="address-edit-btn">
                                  <FiEdit2 size={14} />
                                </button>
                                <button onClick={() => handleDeleteAddress(address._id)} className="address-delete-btn">
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {savedAddresses.length >= 10 && <p className="address-limit-msg">Maximum 10 addresses reached. Delete one to add new.</p>}
              </div>
            </>
            {/* )} */}

            {/* Self Pickup Warehouse Selection - Commented out since self-pickup is disabled */}
            {/* {shippingMethod === 'self-pickup' && (
              <div className="self-pickup-section">
                <h3 className="self-pickup-title">Select Pickup Warehouse</h3>
                {loadingWarehouses ? (
                  <div className="self-pickup-loading">Loading warehouses...</div>
                ) : warehouses.length === 0 ? (
                  <div className="self-pickup-empty">
                    <p>No active warehouses available for pickup. Please contact support.</p>
                  </div>
                ) : (
                  <div className="warehouse-list">
                    {warehouses.map((warehouse) => (
                      <div
                        key={warehouse._id}
                        className={`warehouse-card ${selectedWarehouseId === warehouse._id ? 'warehouse-selected' : ''}`}
                        onClick={() => setSelectedWarehouseId(warehouse._id)}
                      >
                        <div className="warehouse-card-header">
                          <input
                            type="radio"
                            name="warehouse"
                            checked={selectedWarehouseId === warehouse._id}
                            onChange={() => setSelectedWarehouseId(warehouse._id)}
                            className="warehouse-radio"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="warehouse-card-content">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <h4 className="warehouse-name">{warehouse.name}</h4>
                              {selectedWarehouseId === warehouse._id && (
                                <MdCheckCircle className="warehouse-check-icon" size={16} />
                              )}
                            </div>
                            <div className="warehouse-address">
                              <p>{warehouse.address1}</p>
                              {warehouse.address2 && <p>{warehouse.address2}</p>}
                              <p>{warehouse.city}, {warehouse.state} - {warehouse.pincode}</p>
                              {warehouse.landmark && <p className="warehouse-landmark">📍 {warehouse.landmark}</p>}
                            </div>
                            {warehouse.phone && (
                              <p className="warehouse-phone">
                                <FiMapPin size={12} /> {warehouse.phone}
                              </p>
                            )}
                            {warehouse.instructions && (
                              <div className="warehouse-instructions">
                                <strong>Note:</strong> {warehouse.instructions}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )} */}
          </div>

          {/* Right Side - Checkout Box */}
          <div className="lg:col-span-1">
            {/* IM Points Info Tooltip */}
            {/* {imPoints && imAddressPreview && (
              <div
                className="imp-address-points-row"
                ref={imTooltipRef}
                onMouseEnter={() => setImTooltipVisible(true)}
                onMouseLeave={() => setImTooltipVisible(false)}
              >
                <span className="imp-address-points-label">
                  <FaCoins
                    size={11}
                    style={{ color: imAddressPreview.isExpired ? '#ef4444' : '#2563eb', marginRight: 5 }}
                  />
                  IM Points
                  {imAddressPreview.discount > 0 && (
                    <span className="imp-address-points-saving">-₹{imAddressPreview.discount.toLocaleString()}</span>
                  )}
                  {imAddressPreview.isExpired && (
                    <span className="imp-address-points-expired">Expired</span>
                  )}
                </span>
                <FaInfoCircle size={11} style={{ color: '#9ca3af', cursor: 'pointer' }} />
                {imTooltipVisible && (
                  <div className="imp-address-tooltip">
                    {imAddressPreview.isExpired ? (
                      <>
                        <strong>IM Points Expired</strong>
                        <span>
                          Expired on {new Date(imPoints.expiresAt).toLocaleDateString()}. No deduction will apply at
                          checkout.
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>IM Points (estimated)</strong>
                        <span>
                          <strong>₹{imAddressPreview.discount.toLocaleString()}</strong> IM discount —{' '}
                          {imAddressPreview.pct}% of Grand Total ₹
                          {imAddressPreview.grandTotalInclGst.toLocaleString()}.
                        </span>
                        <span>
                          Approx. to pay :{' '}
                          <strong>
                            Final Total ₹{imAddressPreview.finalOrderTotal.toLocaleString()} − IM ₹
                            {imAddressPreview.discount.toLocaleString()} ≈ ₹
                            {imAddressPreview.payableAfterIm.toLocaleString()}
                          </strong>
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          Grand ₹{imAddressPreview.grandTotalInclGst.toLocaleString()} + Shipping ₹
                          {imAddressPreview.shippingAmount.toLocaleString()} = Final ₹
                          {imAddressPreview.finalOrderTotal.toLocaleString()}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          Available: {imAddressPreview.pts.toLocaleString()} Points · Expires:{' '}
                          {new Date(imPoints.expiresAt).toLocaleDateString()}
                        </span>
                        <span style={{ color: '#6b7280' }}>Final amounts are confirmed on the payment step.</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )} */}
            <CheckOutBox
              type="address"
              proceedToPayment={proceedToPayment}
              cart={cart}
              allCartItems={cart}
              hasAllProductCardsMinMOQ={true}
              isAllLoadingCard={{ all: false }}
              cartTotalFinalPrice={calculatedTotals?.cartTotalFinalPrice || 0}
              cartTotalLMD={calculatedTotals?.cartTotalLMD || 0}
              cartLMD={calculatedTotals?.cartLMD || 0}
              // Pass fallback state to ensure CheckOutBox has proper data
              location={fallbackState ? { state: fallbackState } : location}
            />
          </div>
        </div>
      </div>

      {/* Address Form Modal */}
      <AlertPopup
        modalIsOpen={showAddressModal}
        // title={modalMode === 'add' ? 'Add New Address' : 'Edit Address'}
        onClose={() => {
          setShowAddressModal(false);
          setCurrentAddress(null);
        }}
      >
        <AddressForm
          addressData={currentAddress}
          onSubmit={handleAddressSubmit}
          onCancel={() => {
            setShowAddressModal(false);
            setCurrentAddress(null);
          }}
          onClose={() => {
            setShowAddressModal(false);
            setCurrentAddress(null);
          }}
          submitText={modalMode === 'add' ? 'Add Address' : 'Update Address'}
        />
      </AlertPopup>

      {/* Delete Confirmation Modal */}
      <DeletePopUp
        open={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setAddressToDelete(null);
        }}
        heading="Delete Address"
        text="Are you sure you want to delete this address? This action cannot be undone."
        submitText="Delete"
        cancelText="Cancel"
        handleSubmit={confirmDelete}
        handleCancel={() => {
          setShowDeleteModal(false);
          setAddressToDelete(null);
        }}
      />
    </div>
  );
}
